import React, { FC, useEffect, ReactText } from 'react'
import cx from 'classnames'
import { useSelect } from 'downshift'
import { Icon } from 'components/Icon'
import { Text } from 'components/Text'
import styles from './SelectInput.module.scss'

export type SelectOption = { label: string; value: ReactText }

type SelectInputProps = {
  onChange: (value?: SelectOption) => void
  options: SelectOption[]
  label: string
  value?: SelectOption
  disabled?: boolean
  borderless?: boolean
}

const itemToString = (item: SelectOption) => (item ? item.label : '')

export const SelectInput: FC<SelectInputProps> = (props) => {
  const { disabled = false, borderless, label, value, options, onChange } = props
  const {
    highlightedIndex,
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    reset,
  } = useSelect({
    selectedItem: value,
    items: options,
    itemToString,
    onStateChange: (changes) => {
      const type = (changes as { type?: string }).type
      if (
        type === useSelect.stateChangeTypes.ItemClick ||
        type === useSelect.stateChangeTypes.MenuKeyDownEnter ||
        type === useSelect.stateChangeTypes.MenuKeyDownSpaceButton
      ) {
        onChange(changes.selectedItem)
      }
    },
  })
  const classes = cx({
    [styles.selectInput]: true,
    [styles.borderless]: borderless,
  })

  const buttonClass = cx({
    [styles.button]: true,
    [styles.open]: isOpen,
    [styles.hasValue]: selectedItem || value,
    [styles.disabled]: disabled,
  })

  useEffect(() => {
    // This is needed to reset the downshift's internal state when ours changes
    if (selectedItem?.value && value == null) {
      reset()
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes}>
      <button type="button" {...getToggleButtonProps({ className: buttonClass, disabled })}>
        <span className={styles.label}>{label}</span>
        {selectedItem && <Text className={styles.value}>{itemToString(selectedItem)}</Text>}
        <span className={styles.arrowContainer}>
          <Icon name="arrow-drop-down-line" />
        </span>
      </button>
      <ul {...getMenuProps({ className: styles.menu })}>
        {isOpen &&
          options.map((item, index) => (
            <li
              {...getItemProps({
                key: item.label,
                item,
                index,
                className: cx({
                  [styles.menuItem]: true,
                  [styles.selected]: selectedItem && item.value === selectedItem.value,
                  [styles.highlighted]: highlightedIndex === index,
                }),
              })}
            >
              {item.label}
            </li>
          ))}
      </ul>
    </div>
  )
}

SelectInput.displayName = 'SelectInput'
